import { useState, useEffect } from "react";
import { api } from "../../../lib/api";
import { fmtDT } from "../../../lib/constants";
import { toast } from "sonner";
import { UploadSimple, CheckCircle, XCircle, FileText, ArrowsClockwise, EnvelopeSimple } from "@phosphor-icons/react";
import { EvidenceMeta, Panel, friendlyEvent } from "./_shared";

export function DocumentsPanel({ d, reload, isAdmin, isSuperAdmin, isSourceCtop, isSourceCap, isSender, senderReady }) {
  const onUpload = async (docType, file) => {
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/shipments/${d.shipment.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${docType}`);
      reload();
    } catch (e) { toast.error(e.displayMessage); }
  };

  // Area 3 — upload authority mirrors backend: INV = sender before readiness (or SA); INS = SA only.
  // D-08: generic AWB upload retired (per-AWB camera evidence in the AWB section).
  const canUpload = (t) => {
    if (t === "INS") return !!isSuperAdmin;
    // INV
    if (isSuperAdmin) return true;
    return !!(isSender && !senderReady);
  };
  const uploadableTypes = ["INV", "INS"].filter(canUpload);

  // Area 3 — review authority: source docs = source CTOP/SA; others = SA.
  const canReview = (docType) => {
    if (["INV", "AWB"].includes(docType)) return !!(isSuperAdmin || isSourceCtop);
    return !!isSuperAdmin;
  };

  const onReview = async (docId, action) => {
    let reason = null;
    if (action === "REJECT") {
      reason = window.prompt("Rejection reason (min 4 chars):", "");
      if (!reason) return;
    }
    try {
      await api.post(`/shipments/${d.shipment.id}/documents/${docId}/review`, { action, rejection_reason: reason });
      toast.success(`Document ${action}D`);
      reload();
    } catch (e) { toast.error(e.displayMessage); }
  };

  return (
    <Panel icon={<FileText size={16} weight="bold" />} title={`DOCUMENTS (${d.documents.length})`}>
      <div className="flex flex-wrap gap-2 mb-3">
        {uploadableTypes.map((t) => (
          <label key={t} className="btn-ghost cursor-pointer" data-testid={`upload-${t}-button`}>
            <UploadSimple size={14} /> {t}
            <input
              type="file"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onUpload(t, e.target.files[0])}
            />
          </label>
        ))}
      </div>
      {d.documents.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="no-documents">No documents uploaded.</p>
      ) : (
        <table className="table-air12">
          <thead><tr><th>Doc ID</th><th>Type</th><th>Status</th><th>File</th><th>Actions</th></tr></thead>
          <tbody>
            {d.documents.map((doc) => (
              <tr key={doc.id} data-testid={`doc-row-${doc.public_document_id}`}>
                <td><span className="id-chip text-xs">{doc.public_document_id}</span></td>
                <td className="mono text-xs">{doc.doc_type} v{doc.version}<EvidenceMeta doc={doc} /></td>
                <td>
                  {doc.status === "APPROVED" ? <span className="status-pill status-success">APPROVED</span> :
                   doc.status === "REJECTED" ? <span className="status-pill status-error">REJECTED</span> :
                   <span className="status-pill status-warning">PENDING</span>}
                </td>
                <td className="text-xs">{doc.file_name} <span className="text-[hsl(var(--muted))]">({(doc.size_bytes/1024).toFixed(1)} KB)</span></td>
                <td className="flex gap-2">
                  {canReview(doc.doc_type) && doc.status === "PENDING_REVIEW" && (
                    <>
                      <button className="btn-ghost text-xs" onClick={() => onReview(doc.id, "APPROVE")} data-testid={`approve-${doc.public_document_id}`}>
                        <CheckCircle size={12} /> Approve
                      </button>
                      <button className="btn-ghost text-xs" onClick={() => onReview(doc.id, "REJECT")} data-testid={`reject-${doc.public_document_id}`}>
                        <XCircle size={12} /> Reject
                      </button>
                    </>
                  )}
                  {doc.rejection_reason && <span className="text-xs text-[hsl(var(--error))]">{doc.rejection_reason}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export function NotificationsPanel({ d, canResend }) {
  const sid = d.shipment.id;
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const load = () => {
    setLoading(true);
    api.get(`/admin/shipments/${sid}/notifications`)
      .then((r) => setEmails(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, [sid]);
  const resend = async (row) => {
    if (!row.resend_eligible) { toast.error("This email type cannot be resent here."); return; }
    if (!window.confirm("Resend this notification email to its recipient?")) return;
    setBusy(row.id);
    try {
      const { data } = await api.post(`/admin/shipments/${sid}/notifications/resend`, { email_log_id: row.id });
      toast.success(`Notification resent (${data.status})`);
      load();
    } catch (e) { toast.error(e.displayMessage || "Resend failed"); }
    finally { setBusy(null); }
  };
  // Legacy paid-shipment insurance recovery (SUPER_ADMIN only): MAIN completed + an eligible Goods
  // Invoice now exists + no successful insurance request yet. Uses the REAL insurance path.
  const insuranceRecoveryEligible = !!canResend
    && d.shipment.air12_payment_status === "COMPLETED"
    && d.shipment.has_goods_invoice
    && !d.shipment.insurance_request_sent;
  const resendInsurance = async () => {
    if (!window.confirm("Resend the insurance request to PolicyBazaar with the current Goods Invoice attached?")) return;
    setBusy("insurance");
    try {
      const { data } = await api.post(`/admin/shipments/${sid}/insurance/resend`);
      toast.success(data.ok ? "Insurance request sent" : `Insurance resend: ${data.status}`);
      load();
    } catch (e) { toast.error(e.displayMessage || "Insurance resend failed"); }
    finally { setBusy(null); }
  };
  const statusCls = (s) => s === "FAILED" ? "text-red-400" : s === "SENT" ? "text-emerald-400" : "text-amber-400";
  return (
    <Panel icon={<EnvelopeSimple size={16} weight="bold" />} title={`NOTIFICATIONS (${emails.length})`}>
      {insuranceRecoveryEligible && (
        <div className="mb-3 px-3 py-2 rounded bg-[hsl(var(--accent)/0.08)] border border-[hsl(var(--accent)/0.3)]" data-testid="insurance-recovery-box">
          <div className="text-xs font-semibold">Insurance request not yet sent</div>
          <div className="text-[11px] text-[hsl(var(--muted))] mt-0.5">
            Payment is complete and a Goods Invoice is on file. Send the insurance request to PolicyBazaar with that invoice attached.
          </div>
          <button className="btn-secondary text-xs mt-2" disabled={busy === "insurance"} onClick={resendInsurance} data-testid="resend-insurance-request-btn">
            <ArrowsClockwise size={12} /> {busy === "insurance" ? "Sending…" : "Resend insurance request"}
          </button>
        </div>
      )}
      {loading ? (
        <div className="text-xs mono text-[hsl(var(--muted))]">Loading…</div>
      ) : emails.length === 0 ? (
        <div className="text-xs text-[hsl(var(--muted))]" data-testid="notifications-empty">No notifications sent yet.</div>
      ) : (
        <div className="space-y-2 max-h-[360px] overflow-auto" data-testid="notifications-list">
          {emails.map((m) => (
            <div key={m.id} className="text-xs border border-[hsl(var(--border))] rounded p-2" data-testid={`notification-row-${m.id.slice(0, 6)}`}>
              <div className="font-medium">{m.subject}</div>
              <div className="text-[11px] text-[hsl(var(--muted))] mt-0.5 mono">
                {m.trigger} · {m.recipient} · <span className={statusCls(m.status)} data-testid={`notification-status-${m.id.slice(0, 6)}`}>{m.status}</span> · {fmtDT(m.created_at)}
              </div>
              {m.status === "FAILED" && m.error && (
                <button className="text-[11px] text-red-400 underline mt-1" onClick={() => setExpanded(expanded === m.id ? null : m.id)} data-testid={`notification-error-toggle-${m.id.slice(0, 6)}`}>
                  {expanded === m.id ? "Hide error" : "Show error"}
                </button>
              )}
              {expanded === m.id && m.error && (
                <pre className="text-[10px] text-red-400 whitespace-pre-wrap mt-1" data-testid={`notification-error-${m.id.slice(0, 6)}`}>{m.error}</pre>
              )}
              {canResend && (
                m.resend_eligible ? (
                  <button className="btn-ghost text-xs mt-1.5" disabled={busy === m.id} onClick={() => resend(m)} data-testid={`notification-resend-${m.id.slice(0, 6)}`}>
                    <ArrowsClockwise size={12} /> {busy === m.id ? "Resending…" : "Resend"}
                  </button>
                ) : m.trigger === "INSURANCE_REQUEST" ? (
                  <div className="text-[10px] text-[hsl(var(--muted))] mt-1.5" data-testid={`notification-insurance-note-${m.id.slice(0, 6)}`}>Use “Resend insurance request” above (attaches the Goods Invoice + CC).</div>
                ) : (
                  <div className="text-[10px] text-[hsl(var(--muted))] mt-1.5" data-testid={`notification-resend-blocked-${m.id.slice(0, 6)}`}>OTP email · resend via verification flow only</div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export function AuditTimeline({ d }) {
  const PKG_TYPE_LABELS = {
    CARTON_BOX: "Carton / Box", BAG_SACK: "Bag / Sack", CRATE: "Crate",
    BUNDLE: "Bundle", ROLL_TUBE: "Roll / Tube", OTHER: "Other",
  };
  const dims = (m) => (m ? `${m.length_cm} × ${m.width_cm} × ${m.height_cm} cm · ${PKG_TYPE_LABELS[m.observed_package_type] || m.observed_package_type}` : "—");
  return (
    <Panel icon={<ArrowsClockwise size={16} weight="bold" />} title={`AUDIT TIMELINE (${d.events.length})`}>
      <div className="space-y-3 max-h-[420px] overflow-auto">
        {d.events.map((e, idx) => {
          // P4-D27 — dedicated renderer for the correction event (full vs generic per backend shaping).
          if (e.event_type === "RIDER_MEASUREMENT_CORRECTED") {
            const full = !!e.meta;
            return (
              <div key={`rmc-${idx}`} className="text-xs border-l-2 border-[hsl(var(--border-strong))] pl-3" data-testid={`event-RIDER_MEASUREMENT_CORRECTED-${idx}`}>
                <div className="font-semibold" data-testid="event-friendly">Pickup measurement corrected</div>
                {e.public_label && <div className="mono text-[11px]" data-testid="correction-pkg-label">{e.public_label}</div>}
                {full && e.meta?.public_label && <div className="mono text-[11px]" data-testid="correction-pkg-label">{e.meta.public_label}</div>}
                {full && (
                  <>
                    <div className="mt-1"><span className="text-[hsl(var(--muted))]">Previous: </span><span className="mono" data-testid="correction-previous">{dims(e.meta.previous)}</span></div>
                    <div><span className="text-[hsl(var(--muted))]">Corrected: </span><span className="mono" data-testid="correction-new">{dims(e.meta.new)}</span></div>
                    {e.reason && <div className="mt-1 italic" data-testid="correction-reason">"{e.reason}"</div>}
                    <div className="text-[hsl(var(--muted))] mt-1" data-testid="correction-actor">{e.performed_by_email || e.performed_by_role || "SYSTEM"}{e.performed_by_email && e.performed_by_role ? ` (${e.performed_by_role})` : ""}</div>
                  </>
                )}
                <div className="text-[hsl(var(--muted))] mt-1">{fmtDT(e.occurred_at)}</div>
              </div>
            );
          }
          const friendly = friendlyEvent(e);
          return (
          <div key={e.id} className="text-xs border-l-2 border-[hsl(var(--border-strong))] pl-3" data-testid={`event-${e.event_type}-${e.id.slice(0,4)}`}>
            {friendly ? (
              <>
                <div className="font-semibold" data-testid="event-friendly">{friendly}</div>
                <div className="text-[10px] mono text-[hsl(var(--muted))]">{e.event_type}</div>
              </>
            ) : (
              <div className="font-semibold mono">{e.event_type}</div>
            )}
            {e.old_status && e.new_status && (
              <div className="text-[hsl(var(--muted))] mono">{e.old_status} → {e.new_status}</div>
            )}
            {e.reason && <div className="mt-1 italic">"{e.reason}"</div>}
            <div className="text-[hsl(var(--muted))] mt-1">{fmtDT(e.occurred_at)} · {e.performed_by_email || e.performed_by_role || "SYSTEM"}{e.performed_by_email && e.performed_by_role ? ` (${e.performed_by_role})` : ""}</div>
          </div>
          );
        })}
        {d.events.length === 0 && <p className="text-sm text-[hsl(var(--muted))]">No events.</p>}
      </div>
    </Panel>
  );
}


// Permanent later re-download of the Quote Acceptance Record + the governing Air12 Sender Agreement.
// Renders ONLY for the Shipment-owning Sender / Super Admin (the backend status + document routes
// are owner/SA-scoped and authoritative). Resolves the ORIGINAL persisted generated documents via
// the existing authorised routes — no regeneration, no newer-template substitution, no new acceptance.
export function LegalDocumentsPanel({ shipmentId }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/booking/${shipmentId}/agreement/status`)
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => { if (alive) setStatus(null); })       // 404 for non-owner / no acceptance → hidden
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [shipmentId]);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await api.get(`/booking/${shipmentId}/agreement/document/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.displayMessage || "Could not open the document. Please try again shortly.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) return null;                 // stay quiet until availability is known (no empty flash)
  if (!status || !status.accepted) return null;   // no completed acceptance → no section

  return (
    <Panel icon={<FileText size={16} />} title="Legal documents">
      <p className="text-xs text-[hsl(var(--muted))] mb-3" data-testid="legal-documents-note">
        Quote accepted{status.record_id ? ` (${status.record_id})` : ""}
        {status.accepted_at ? ` on ${fmtDT(status.accepted_at)}` : ""}.
      </p>
      <div className="flex flex-wrap gap-3">
        <button className="btn-ghost text-sm" disabled={busy === "agreement"}
                onClick={() => download("agreement")} data-testid="legal-download-sender-agreement">
          {busy === "agreement" ? "Opening…" : "Download Air12 Sender Agreement"}
        </button>
        <button className="btn-ghost text-sm" disabled={busy === "acceptance_record"}
                onClick={() => download("acceptance_record")} data-testid="legal-download-acceptance-record">
          {busy === "acceptance_record" ? "Opening…" : "Download Quote Acceptance Record"}
        </button>
      </div>
    </Panel>
  );
}

